import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import {
  Loader2, Plus, Pencil, Trash2, CheckCircle2, AlertTriangle,
  Sparkles, Users, X,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Persona {
  id: number
  name: string
  first_names: string[] | null
  complements: string[] | null
  descriptions: string[] | null
  media_tag: string | null
  page_category: string | null
}

interface CreationConfig {
  enabled: boolean
  daily_target: number
  monthly_target: number
  interval_min: number
  interval_max: number
  active_start: string
  active_end: string
}

interface CreatedAsset {
  id: number
  asset_type: 'page' | 'group'
  fb_name: string | null
  fb_url: string | null
  status: 'created' | 'failed'
  error_code: string | null
  created_at: string
  persona?: { id: number; name: string } | null
}

interface Progress {
  today: number
  month: number
  daily_target: number
  monthly_target: number
}

interface ProfileLite {
  id: string
  name: string
  persona_id: number | null
}

interface FolderLite {
  id: string
  name: string
  tags: string[]
}

/* ------------------------------------------------------------------ */
/*  Helpers + shared classes                                          */
/* ------------------------------------------------------------------ */

const linesToArray = (s: string) => s.split('\n').map(x => x.trim()).filter(Boolean)
const arrayToLines = (a: string[] | null) => (a ?? []).join('\n')

const CARD = 'rounded-2xl border border-zinc-800 bg-zinc-900 p-5'
const INPUT = 'rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-indigo-500'
const LABEL = 'text-xs font-medium text-zinc-500'

function ProgressBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
  return (
    <div className="h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Persona modal                                                     */
/* ------------------------------------------------------------------ */

function PersonaModal({
  persona, tags, onClose, onSaved,
}: {
  persona: Persona | null
  tags: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState(persona?.name ?? '')
  const [category, setCategory] = useState(persona?.page_category ?? '')
  const [mediaTag, setMediaTag] = useState(persona?.media_tag ?? '')
  const [firstNames, setFirstNames] = useState(arrayToLines(persona?.first_names ?? null))
  const [complements, setComplements] = useState(arrayToLines(persona?.complements ?? null))
  const [descriptions, setDescriptions] = useState(arrayToLines(persona?.descriptions ?? null))

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        page_category: category,
        media_tag: mediaTag || null,
        first_names: linesToArray(firstNames),
        complements: linesToArray(complements),
        descriptions: linesToArray(descriptions),
      }
      return persona
        ? api.put(`/automacao-fb/personas/${persona.id}`, body)
        : api.post('/automacao-fb/personas', body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'personas'] })
      onSaved()
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-900 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
          <h3 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: '#8b5cf6' }} />
            {persona ? 'Editar persona' : 'Nova persona'}
          </h3>
          <button onClick={onClose} className="rounded-lg p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Nome interno</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Mônica"
                className={`mt-1 w-full ${INPUT}`} />
            </div>
            <div>
              <label className={LABEL}>Categoria da página</label>
              <input value={category} onChange={e => setCategory(e.target.value)} placeholder="Investimentos"
                className={`mt-1 w-full ${INPUT}`} />
            </div>
          </div>

          <div>
            <label className={LABEL}>Tag de mídia (puxa imagens da library)</label>
            <select value={mediaTag} onChange={e => setMediaTag(e.target.value)}
              className={`mt-1 w-full ${INPUT}`}>
              <option value="">— sem tag —</option>
              {tags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Primeiros nomes (1/linha)</label>
              <textarea value={firstNames} onChange={e => setFirstNames(e.target.value)} rows={5}
                placeholder={'Ana\nMaria\nLéo'} className={`mt-1 w-full font-mono ${INPUT}`} />
            </div>
            <div>
              <label className={LABEL}>Complementos (1/linha)</label>
              <textarea value={complements} onChange={e => setComplements(e.target.value)} rows={5}
                placeholder={'Investe\nTrader\nMoney'} className={`mt-1 w-full font-mono ${INPUT}`} />
            </div>
          </div>

          <div>
            <label className={LABEL}>Descrições/bios (1/linha)</label>
            <textarea value={descriptions} onChange={e => setDescriptions(e.target.value)} rows={3}
              placeholder={'Dicas de investimento todos os dias'} className={`mt-1 w-full ${INPUT}`} />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-zinc-800 px-5 py-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-zinc-300 hover:bg-zinc-800">Cancelar</button>
          <button onClick={() => save.mutate()} disabled={!name || save.isPending}
            className="rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            style={{ background: '#6366f1' }}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main tab                                                           */
/* ------------------------------------------------------------------ */

export function CreationTab() {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Persona | null>(null)

  const { data: personasRaw, isLoading: loadingPersonas } = useQuery<{ personas: Persona[] }>({
    queryKey: ['automacao-fb', 'personas'],
    queryFn: () => api.get('/automacao-fb/personas'),
  })
  const { data: configRaw } = useQuery<{ config: CreationConfig }>({
    queryKey: ['automacao-fb', 'creation-config'],
    queryFn: () => api.get('/automacao-fb/creation-config'),
  })
  const { data: assetsRaw } = useQuery<{ assets: CreatedAsset[]; progress: Progress }>({
    queryKey: ['automacao-fb', 'created-assets'],
    queryFn: () => api.get('/automacao-fb/created-assets'),
    refetchInterval: 30000,
  })
  const { data: foldersRaw } = useQuery<{ folders: FolderLite[] }>({
    queryKey: ['automacao-fb', 'media-folders'],
    queryFn: () => api.get('/automacao-fb/media-folders'),
  })
  const { data: profilesRaw } = useQuery<{ profiles: ProfileLite[] }>({
    queryKey: ['automacao-fb', 'profiles'],
    queryFn: () => api.get('/automacao-fb/profiles'),
  })

  const personas = personasRaw?.personas ?? []
  const assets = assetsRaw?.assets ?? []
  const progress = assetsRaw?.progress
  const profiles = profilesRaw?.profiles ?? []

  const tags = useMemo(() => {
    const set = new Set<string>()
    for (const f of foldersRaw?.folders ?? []) for (const t of f.tags ?? []) set.add(t)
    return [...set].sort()
  }, [foldersRaw])

  const [cfg, setCfg] = useState<CreationConfig | null>(null)
  useEffect(() => { if (configRaw?.config) setCfg(configRaw.config) }, [configRaw])

  const saveConfig = useMutation({
    mutationFn: (c: CreationConfig) => api.put('/automacao-fb/creation-config', c),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automacao-fb', 'creation-config'] }),
  })
  const deletePersona = useMutation({
    mutationFn: (id: number) => api.delete(`/automacao-fb/personas/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'personas'] })
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'profiles'] })
    },
  })
  const assignPersona = useMutation({
    mutationFn: ({ profileId, personaId }: { profileId: string; personaId: number | null }) =>
      api.put(`/automacao-fb/profiles/${profileId}`, { persona_id: personaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automacao-fb', 'profiles'] }),
  })

  return (
    <div className="space-y-6">
      {/* Top row: progresso + config */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Progresso */}
        <div className={CARD}>
          <h3 className="mb-4 text-sm font-semibold text-zinc-200">Progresso da meta</h3>
          {progress ? (
            <div className="space-y-4">
              <div>
                <div className="mb-1 flex justify-between text-sm">
                  <span className="text-zinc-400">Hoje</span>
                  <span className="font-semibold text-zinc-100">{progress.today}/{progress.daily_target}</span>
                </div>
                <ProgressBar value={progress.today} max={progress.daily_target} color="#6366f1" />
              </div>
              <div>
                <div className="mb-1 flex justify-between text-sm">
                  <span className="text-zinc-400">Mês</span>
                  <span className="font-semibold text-zinc-100">{progress.month}/{progress.monthly_target}</span>
                </div>
                <ProgressBar value={progress.month} max={progress.monthly_target} color="#8b5cf6" />
              </div>
            </div>
          ) : <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />}
        </div>

        {/* Config */}
        <div className={CARD}>
          <h3 className="mb-4 text-sm font-semibold text-zinc-200">Configuração</h3>
          {cfg ? (
            <div className="space-y-3">
              <label className="flex items-center justify-between">
                <span className="text-sm text-zinc-300">Criação ativa</span>
                <input type="checkbox" checked={cfg.enabled}
                  onChange={e => setCfg({ ...cfg, enabled: e.target.checked })}
                  className="h-5 w-9 accent-indigo-500" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Meta diária</label>
                  <input type="number" value={cfg.daily_target}
                    onChange={e => setCfg({ ...cfg, daily_target: +e.target.value })}
                    className={`mt-1 w-full ${INPUT}`} />
                </div>
                <div>
                  <label className={LABEL}>Meta mensal</label>
                  <input type="number" value={cfg.monthly_target}
                    onChange={e => setCfg({ ...cfg, monthly_target: +e.target.value })}
                    className={`mt-1 w-full ${INPUT}`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Intervalo min (min)</label>
                  <input type="number" value={cfg.interval_min}
                    onChange={e => setCfg({ ...cfg, interval_min: +e.target.value })}
                    className={`mt-1 w-full ${INPUT}`} />
                </div>
                <div>
                  <label className={LABEL}>Intervalo max (min)</label>
                  <input type="number" value={cfg.interval_max}
                    onChange={e => setCfg({ ...cfg, interval_max: +e.target.value })}
                    className={`mt-1 w-full ${INPUT}`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Início (HH:MM)</label>
                  <input value={cfg.active_start}
                    onChange={e => setCfg({ ...cfg, active_start: e.target.value })}
                    className={`mt-1 w-full ${INPUT}`} />
                </div>
                <div>
                  <label className={LABEL}>Fim (HH:MM)</label>
                  <input value={cfg.active_end}
                    onChange={e => setCfg({ ...cfg, active_end: e.target.value })}
                    className={`mt-1 w-full ${INPUT}`} />
                </div>
              </div>
              <button onClick={() => saveConfig.mutate(cfg)} disabled={saveConfig.isPending}
                className="mt-1 w-full rounded-lg py-2 text-sm font-medium text-white disabled:opacity-50"
                style={{ background: '#6366f1' }}>
                {saveConfig.isPending ? 'Salvando…' : 'Salvar config'}
              </button>
            </div>
          ) : <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />}
        </div>
      </div>

      {/* Personas */}
      <div className={CARD}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
            <Sparkles className="h-4 w-4" style={{ color: '#8b5cf6' }} /> Personas
          </h3>
          <button onClick={() => { setEditing(null); setModalOpen(true) }}
            className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-white"
            style={{ background: '#6366f1' }}>
            <Plus className="h-4 w-4" /> Nova
          </button>
        </div>
        {loadingPersonas ? (
          <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
        ) : personas.length === 0 ? (
          <p className="text-sm text-zinc-500">Nenhuma persona ainda. Crie a primeira.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {personas.map(p => (
              <div key={p.id} className="rounded-xl border border-zinc-700 bg-zinc-800/50 p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-zinc-100">{p.name}</p>
                    <p className="text-xs text-zinc-500">{p.page_category || '—'}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(p); setModalOpen(true) }}
                      className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => { if (confirm(`Excluir persona "${p.name}"?`)) deletePersona.mutate(p.id) }}
                      className="rounded-lg p-1.5 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1 text-[11px] text-zinc-400">
                  <span className="rounded bg-zinc-800 px-1.5 py-0.5 border border-zinc-700">{(p.first_names ?? []).length} nomes</span>
                  <span className="rounded bg-zinc-800 px-1.5 py-0.5 border border-zinc-700">{(p.complements ?? []).length} compl.</span>
                  {p.media_tag && <span className="rounded bg-zinc-800 px-1.5 py-0.5 border border-zinc-700">tag: {p.media_tag}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Atribuir persona ao perfil */}
      <div className={CARD}>
        <h3 className="mb-4 text-sm font-semibold text-zinc-200 flex items-center gap-2">
          <Users className="h-4 w-4" style={{ color: '#8b5cf6' }} /> Persona por perfil
        </h3>
        {profiles.length === 0 ? (
          <p className="text-sm text-zinc-500">Nenhum perfil cadastrado.</p>
        ) : (
          <div className="space-y-2">
            {profiles.map(pf => (
              <div key={pf.id} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-800/30 px-3 py-2">
                <span className="text-sm text-zinc-300">{pf.name}</span>
                <select value={pf.persona_id ?? ''}
                  onChange={e => assignPersona.mutate({ profileId: pf.id, personaId: e.target.value ? +e.target.value : null })}
                  className={INPUT}>
                  <option value="">— sem persona —</option>
                  {personas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Histórico */}
      <div className={CARD}>
        <h3 className="mb-4 text-sm font-semibold text-zinc-200">Histórico de criações</h3>
        {assets.length === 0 ? (
          <p className="text-sm text-zinc-500">Nenhuma criação ainda.</p>
        ) : (
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {assets.map(a => (
              <div key={a.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-zinc-800">
                {a.status === 'created'
                  ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  : <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />}
                <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[11px] font-medium uppercase text-zinc-400">
                  {a.asset_type === 'page' ? 'Página' : 'Grupo'}
                </span>
                {a.fb_url ? (
                  <a href={a.fb_url} target="_blank" rel="noreferrer" className="truncate text-zinc-300 hover:text-indigo-400 hover:underline">
                    {a.fb_name || a.fb_url}
                  </a>
                ) : (
                  <span className="truncate text-zinc-300">{a.fb_name || (a.error_code ?? 'falhou')}</span>
                )}
                {a.persona && <span className="ml-auto shrink-0 text-[11px] text-zinc-500">{a.persona.name}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <PersonaModal persona={editing} tags={tags}
          onClose={() => setModalOpen(false)}
          onSaved={() => setModalOpen(false)} />
      )}
    </div>
  )
}
